import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { TFunction } from "@/i18n/catalog";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatDateRange } from "@/lib/format";
import { weekEndDay, weekStartDay, type DayRange } from "@/lib/zoned-day";
import { showApiErrorAs } from "@/lib/api-error";
import {
  useFoodSpendPeriods,
  useCreateFoodSpend,
  useUpdateFoodSpend,
  useDeleteFoodSpend,
  type FoodSpendPeriod,
} from "@/hooks/use-food-spend";

type Props = {
  open: boolean;
  onClose: () => void;
  /** The dashboard's active site — every week is recorded against it (POS2-996). Null only
   *  before the sites load or for an org with none, when there is nothing to record against. */
  siteId: string | null;
  /** That site's name, shown in the title so the scope is never in doubt. */
  siteName: string | null;
  /** Resolved org/site currency — the default for new weeks. */
  currency: string;
  /** Dashboard window as site-local civil days — defaults the "week" picker to the viewed week. */
  dayRange: DayRange;
  /** Today in the site's zone — the picker's fallback when the window is unbounded. */
  today: string;
};

// One number per Mon–Sun week (POS2-955): the user picks any day and it snaps to
// that day's week. Amount is the whole-week spend.
/** `food_spend_periods.amount` is `numeric(12, 2)` — ten digits before the point. Past that the
 *  value overflows the column and the write comes back a 500, so the form stops it here
 *  (POS2-1068). */
const AMOUNT_MAX = 9_999_999_999.99;

function makeSchema(t: TFunction) {
  return z.object({
    week: z.string().min(1, t("dashboard.coversPickWeek")),
    amount: z
      .number({ invalid_type_error: t("dashboard.spendEnterAmount") })
      .positive(t("dashboard.spendPositive"))
      .max(AMOUNT_MAX, t("dashboard.spendTooLarge")),
    currency: z.string().min(1, t("dashboard.required")),
  });
}

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

/** The Mon–Sun bounds of the week containing `day` (yyyy-MM-dd), as yyyy-MM-dd. */
function weekBounds(day: string): { start: string; end: string } {
  return { start: weekStartDay(day), end: weekEndDay(day) };
}

export function FoodSpendDialog({
  open,
  onClose,
  siteId,
  siteName,
  currency,
  dayRange,
  today,
}: Props) {
  const { locale, t } = useTranslation();
  const schema = useMemo(() => makeSchema(t), [t]);
  // All weeks for the site, decoupled from the dashboard range (POS2-955).
  const periodsQuery = useFoodSpendPeriods(siteId, { from: null, to: null }, today);
  const createMutation = useCreateFoodSpend();
  const updateMutation = useUpdateFoodSpend();
  const deleteMutation = useDeleteFoodSpend();

  const [editingId, setEditingId] = useState<string | null>(null);

  const defaults: Partial<FormValues> = {
    week: dayRange.from ?? today,
    currency,
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: defaults });

  // RHF defaultValues only apply on first mount, but this dialog stays mounted while
  // closed — so re-prefill (site, viewed week, currency) each time it opens.
  useEffect(() => {
    if (open) {
      setEditingId(null);
      reset(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startEdit = (p: FoodSpendPeriod) => {
    setEditingId(p.id);
    reset({ week: p.period_start, amount: p.amount, currency: p.currency });
  };

  const cancelEdit = () => {
    setEditingId(null);
    reset(defaults);
  };

  // Only rendered with a site (see the guard below), so the payload's site is never in doubt.
  const onSubmit = (values: FormValues) => {
    if (!siteId || clash) return;
    const { start, end } = weekBounds(values.week);
    const payload = {
      site_id: siteId,
      period_start: start,
      period_end: end,
      amount: values.amount,
      currency: values.currency,
    };
    // One figure per week: submitting for a week that already has one updates that row.
    const targetId = editingId ?? existing?.id ?? null;
    if (targetId) {
      updateMutation.mutate(
        { id: targetId, ...payload },
        {
          onSuccess: () => {
            toast.success(t("dashboard.spendUpdated"));
            cancelEdit();
          },
          onError: (e: Error) => showApiErrorAs(t("dashboard.foodSpendSaveFailed"), e),
        },
      );
      return;
    }
    createMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(t("dashboard.spendAdded"));
        reset(defaults);
      },
      onError: (e: Error) => showApiErrorAs(t("dashboard.foodSpendSaveFailed"), e),
    });
  };

  const remove = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success(t("dashboard.spendRemoved"));
        if (editingId === id) cancelEdit();
      },
      onError: (e: Error) => showApiErrorAs(t("dashboard.foodSpendDeleteFailed"), e),
    });
  };

  // Most recent week first (period_start is a yyyy-MM-dd string → lexical == chronological).
  const periods = useMemo(
    () =>
      [...(periodsQuery.data ?? [])].sort((a, b) => b.period_start.localeCompare(a.period_start)),
    [periodsQuery.data],
  );
  const pickedWeek = watch("week");
  const pickedBounds = pickedWeek ? weekBounds(pickedWeek) : null;
  const weekLabel = pickedBounds ? `${pickedBounds.start} – ${pickedBounds.end}` : null;
  // One figure per week, enforced here because nothing below the UI enforces it: the API
  // inserts unconditionally and the table has no unique key on (site, period). A duplicate
  // week is summed on the card *and* stacks per-day in the Waste % denominator, so it
  // silently halves the %. So a submit for a week that already has a figure edits that
  // figure rather than adding a second one (POS2-996).
  const existing = pickedBounds
    ? periods.find(
        (p) => p.period_start === pickedBounds.start && p.period_end === pickedBounds.end,
      )
    : undefined;
  // Re-dating one week's figure onto a week that already has its own would merge two
  // amounts with no way to say which wins — refuse instead of picking one.
  const clash = editingId != null && existing != null && existing.id !== editingId;
  const isPending = createMutation.isPending || updateMutation.isPending;

  // No site to record against (sites still loading, or an org with none). The dashboard
  // resolves one for every other case, so there is no site picker here (POS2-996).
  if (!siteId) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("dashboard.spendTitle")}</DialogTitle>
            <DialogDescription>{t("dashboard.spendNoSite")}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {siteName
              ? t("dashboard.spendTitleForSite", { site: siteName })
              : t("dashboard.spendTitle")}
          </DialogTitle>
          <DialogDescription>{t("dashboard.spendDescription")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor="fs-week">
                {t("dashboard.weekLabel")}
              </label>
              <Input id="fs-week" type="date" {...register("week")} />
              {weekLabel && (
                <p className="mt-1 text-xs text-gray-400">
                  {t("dashboard.weekRange", { range: weekLabel })}
                </p>
              )}
              {errors.week && <p className="mt-1 text-xs text-red-600">{errors.week.message}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor="fs-amount">
                {t("dashboard.spendAmountLabel", { currency })}
              </label>
              <Input
                id="fs-amount"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                {...register("amount", { valueAsNumber: true })}
              />
              {errors.amount && (
                <p className="mt-1 text-xs text-red-600">{errors.amount.message}</p>
              )}
            </div>
          </div>
          <input type="hidden" {...register("currency")} />
          {clash ? (
            <p className="text-xs text-red-600" data-testid="food-spend-week-clash">
              {t("dashboard.spendClash", { week: weekLabel ?? "" })}
            </p>
          ) : (
            existing &&
            !editingId && (
              <p className="text-xs text-gray-500" data-testid="food-spend-week-exists">
                {t("dashboard.spendReplaces", {
                  amount: formatCurrency(locale, existing.amount, existing.currency),
                })}
              </p>
            )
          )}
          <div className="flex justify-end gap-2">
            {editingId && (
              <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>
                {t("common.cancel")}
              </Button>
            )}
            <Button type="submit" size="sm" disabled={isPending || clash}>
              {editingId || existing ? t("dashboard.saveChanges") : t("dashboard.addWeek")}
            </Button>
          </div>
        </form>

        <div className="mt-2 flex flex-col gap-1">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            {t("dashboard.weeks")}
          </p>
          {periodsQuery.isLoading ? (
            <p className="py-4 text-center text-sm text-gray-500">{t("common.loading")}</p>
          ) : periods.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">{t("dashboard.noWeeks")}</p>
          ) : (
            periods.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-black/[0.02]"
                data-testid="food-spend-row"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900 tabular-nums">
                    {formatCurrency(locale, p.amount, p.currency)}
                  </span>
                  {/* The same compact label the food spend KPI card and the base covers weeks
                      list use. Raw ISO here was the odd one out (POS2-1259). */}
                  <span className="text-xs text-gray-400">
                    {formatDateRange(locale, p.period_start, p.period_end)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("dashboard.editWeek")}
                    onClick={() => startEdit(p)}
                  >
                    <Pencil className="size-4 text-gray-400" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("dashboard.deleteWeek")}
                    onClick={() => remove(p.id)}
                  >
                    <Trash2 className="size-4 text-gray-400" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
